import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import ArrivedIcon from '@/assets/smartcity/arrived-icon.svg';
import CallIcon from '@/assets/smartcity/call-icon.svg';
import DispatchIcon from '@/assets/smartcity/dispatch-icon.svg';
import FinishIcon from '@/assets/smartcity/finsih-icon.svg';
import HealthCareIcon from '@/assets/smartcity/healthcare-icon.svg';
import PatientIcon from '@/assets/smartcity/patient-icon.svg';
import RejectIcon from '@/assets/smartcity/reject-icon.svg';
import RequestIcon from '@/assets/smartcity/request-icon.svg';
import TransferIcon from '@/assets/smartcity/transfer-icon.svg';
import { reportDetailsApi } from '@/domains/traffic-monitoring/apis/report-details';
import type { EventTypes } from '@/domains/traffic-monitoring/constants/listEvent.constant';
import type {
  AmbulanceReportMediaItem,
  LocationRequestBody,
  ReportAmbulanceReportData,
  ReportDetailsData,
  ReportDetailsHeaderData,
  ReportDetailsStatus,
  ReportMediaItem,
  ReportOfficerItem,
  ReportPatientData,
  ReportTimelineItemData,
  ReportUpdateBody,
  TimelineEntryView,
} from '@/domains/traffic-monitoring/types/report-details.types';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

/**
 * Wires ReportDetails.tsx (the "Report Details" screen) to api-dsh:
 *  - GET /report/{report_id} (call info, location, patients[], ambulance_reports[], counts)
 *  - GET /report/{report_id}/media (media gallery + voice recordings)
 *  - POST /report/{report_id}/location-request (WhatsApp the reporter a location link)
 *
 * There is no endpoint for "mark report as complete" or for chat history read -
 * both confirmed absent from the backend, so those stay out of scope here.
 */

// The server's own derivation, not a second one guessed from two timestamps.
// The rebuilt version this replaces knew nothing about the ambulances, so a
// report whose crews had all finished still read "Registered" - and it called
// any finished_operator_datetime "Rejected", which turned a completed report
// into a red REJECTED badge. get_report_detail_by_id.py runs the same
// _derive_event_type the PSC dashboard cards use, so the two now always agree.
const deriveStatus = (report: ReportDetailsHeaderData | null): ReportDetailsStatus =>
  report?.event_type ?? 'incoming';

const voiceNoteTitle = (item: ReportMediaItem, index: number, t: TFunction): string => {
  const filename = item.media_filepath?.split('/').pop();
  return filename || t('report>timeline>recording', { number: index + 1 });
};

const formatIso = (iso: string | null): string =>
  iso ? formatFullDateTimeDDMMYYYY(new Date(iso)) : '';

const TIMELINE_ICON_BY_EVENT_CODE: Record<string, string> = {
  RPT_EVT_TP_RCV: CallIcon,
  RPT_EVT_TP_REG: RequestIcon,
  RPT_EVT_TP_REJ: RejectIcon,
  AMB_RPT_ST_REG: RequestIcon,
  AMB_RPT_ST_ACC: DispatchIcon,
  AMB_RPT_ST_REJ: RejectIcon,
  AMB_RPT_ST_ARR: ArrivedIcon,
  AMB_RPT_ST_PAT: PatientIcon,
  AMB_RPT_ST_TRF: TransferIcon,
  AMB_RPT_ST_HSP: HealthCareIcon,
  AMB_RPT_ST_FIN: FinishIcon,
  RPT_ST_COMP: FinishIcon,
};

const vehicleLabel = (item: ReportTimelineItemData, t: TFunction): string =>
  t('report>timeline>ambulance team', {
    vehicle: item.police_number ?? item.serial_no ?? item.ambulance_id,
  });

// event_code + backend facts (health_care_facility_name, position among this
// report's ambulance_reports) -> display title. Backend intentionally sends
// codes/facts, not copy - see CLAUDE.md. The copy itself comes from
// cm_language (System Management > Language) via these tags.
const timelineTitle = (
  item: ReportTimelineItemData,
  isReplacement: boolean,
  t: TFunction
): string => {
  switch (item.event_code) {
    case 'RPT_EVT_TP_RCV':
      return t('report>timeline>report received');
    case 'RPT_EVT_TP_REG':
      return t('report>timeline>registered report');
    case 'AMB_RPT_ST_REG':
      return isReplacement
        ? t('report>timeline>request dispatch replacement')
        : t('report>timeline>request dispatch report');
    case 'AMB_RPT_ST_ACC':
      return t('report>timeline>accept dispatch report');
    case 'AMB_RPT_ST_REJ':
      return t('report>timeline>reject dispatch report');
    case 'AMB_RPT_ST_ARR':
      return t('report>timeline>arrived at incident');
    case 'AMB_RPT_ST_PAT':
      return t('report>timeline>patient being handled');
    case 'AMB_RPT_ST_TRF':
      return item.health_care_facility_name
        ? t('report>timeline>transfer to', { facility: item.health_care_facility_name })
        : t('report>timeline>transfer');
    case 'AMB_RPT_ST_HSP':
      return item.health_care_facility_name ?? t('report>timeline>arrived at healthcare');
    case 'AMB_RPT_ST_FIN':
      return t('report>timeline>finished report');
    case 'RPT_ST_COMP':
      return t('report>timeline>report completed');
    default:
      return item.event_label ?? item.event_code;
  }
};

const timelineSubtitle = (item: ReportTimelineItemData, t: TFunction): string | undefined => {
  if (item.actor_type === 'ambulance') return vehicleLabel(item, t);
  if (item.actor_type === 'operator') return item.officer_name ?? t('report>timeline>operator');
  return undefined;
};

const buildTimeline = (items: ReportTimelineItemData[], t: TFunction): TimelineEntryView[] => {
  const seenAmbulanceReportIds = new Set<number>();
  let nextId = 1;

  const entries = items.map((item) => {
    const isReplacement =
      item.event_code === 'AMB_RPT_ST_REG' &&
      item.ambulance_report_id != null &&
      seenAmbulanceReportIds.size > 0 &&
      !seenAmbulanceReportIds.has(item.ambulance_report_id);

    if (item.ambulance_report_id != null) seenAmbulanceReportIds.add(item.ambulance_report_id);

    return {
      id: nextId++,
      title: timelineTitle(item, isReplacement, t),
      subtitle: timelineSubtitle(item, t),
      date: formatIso(item.at),
      icon: TIMELINE_ICON_BY_EVENT_CODE[item.event_code] ?? RequestIcon,
      status: (item.status ?? undefined) as EventTypes | undefined,
      statusLabel: item.event_label ?? undefined,
    };
  });

  return entries.reverse();
};

export const useReportDetails = (reportId?: number) => {
  const enabled = reportId != null;
  // Timeline copy and recording titles are built here, so they need `t`; the
  // language also keys the queries below because the backend resolves
  // event_label / status labels from the caller's language_preference.
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const {
    report,
    patients,
    ambulanceReports,
    images,
    voiceNotes,
    recordings,
    timeline,
    isLoading,
    isDetailError,
  } = useQueries({
    queries: [
      {
        queryKey: ['reportDetails', reportId, lang],
        queryFn: async () => {
          const res = await reportDetailsApi.getReportDetails(reportId as number);
          return (res?.data as ReportDetailsData | null) ?? null;
        },
        enabled,
        // A 404 means the report is gone (soft-deleted after the list loaded) -
        // retrying it three times just delays the error the caller needs to show.
        retry: (failureCount: number, error: unknown) => {
          const status = (error as { response?: { status?: number } } | undefined)?.response
            ?.status;
          if (status === 404) return false;
          return failureCount < 3;
        },
        refetchInterval: 2000,
      },
      {
        queryKey: ['reportDetailsMedia', reportId],
        queryFn: async () => {
          const res = await reportDetailsApi.getReportMedia(reportId as number);
          return res?.data?.media_list ?? [];
        },
        enabled,
        refetchInterval: 2000,
      },
    ],
    combine: ([detailQuery, mediaQuery]) => {
      const detail = (detailQuery.data as ReportDetailsData | null) ?? null;
      const mediaList = (mediaQuery.data as ReportMediaItem[]) || [];
      return {
        report: detail?.report ?? null,
        patients: (detail?.patients as ReportPatientData[]) ?? [],
        ambulanceReports: (detail?.ambulance_reports as ReportAmbulanceReportData[]) ?? [],
        images: mediaList
          .filter(
            (m) =>
              (m.media_type_id === 'MEDIA_TP_IMG' || m.media_type_id === 'MEDIA_TP_VID') && m.link
          )
          .map((m) => ({
            url: m.link as string,
            type: (m.media_type_id === 'MEDIA_TP_VID' ? 'video' : 'image') as 'video' | 'image',
            // The link is an id route (/media/id/198), so the name has to
            // come from the stored path or there is nothing to label it with.
            name: m.media_filepath?.split('/').pop() ?? '',
            registeredTime: m.registered_time
              ? formatFullDateTimeDDMMYYYY(new Date(m.registered_time))
              : '',
          })),
        voiceNotes: mediaList
          .filter((m) => m.media_type_id === 'MEDIA_TP_REC' && m.link)
          .map((m, index) => ({
            id: m.media_files_id,
            title: voiceNoteTitle(m, index, t),
            audioUrl: m.link as string,
          })),
        // All recordings carrying is_call_recording, so a consumer that
        // needs the Incoming-Calls vs Voice-Notes split (Report Management)
        // can do it without a second media fetch. `voiceNotes` above stays
        // as-is for the other screens.
        recordings: mediaList
          .filter((m) => m.media_type_id === 'MEDIA_TP_REC' && m.link)
          .map((m, index) => ({
            id: m.media_files_id,
            title: voiceNoteTitle(m, index, t),
            audioUrl: m.link as string,
            isCall: m.is_call_recording === true,
          })),
        timeline: buildTimeline((detail?.timeline as ReportTimelineItemData[]) ?? [], t),
        isLoading: detailQuery.isLoading || mediaQuery.isLoading,
        // Without this a failed detail fetch (deleted report -> 404, expired
        // session, network) is indistinguishable from "still loading": report
        // stays null, the caller's form stays locked, and nothing says why.
        isDetailError: detailQuery.isError,
      };
    },
  });

  const status = deriveStatus(report);

  const locationRequestMutation = useMutation({
    mutationFn: (body: LocationRequestBody = {}) =>
      reportDetailsApi.postLocationRequest(reportId as number, body),
  });

  const queryClient = useQueryClient();

  const saveReportMutation = useMutation({
    mutationFn: (body: ReportUpdateBody) =>
      reportDetailsApi.putReportDetails(reportId as number, body),
    onSuccess: () => {
      // Refetch rather than patch the cache: the server normalizes what it
      // stored (trimmed strings, '' -> null, a parsed date), and the page
      // should show what is actually on the record.
      queryClient.invalidateQueries({ queryKey: ['reportDetails', reportId, lang] });
      queryClient.invalidateQueries({ queryKey: ['pscReportsToday'] });
    },
  });

  // Soft delete. The row leaves every report list, so both the management list
  // and the PSC dashboard have to be refetched; the detail cache for this id is
  // dropped rather than invalidated, since refetching a deleted report 404s.
  const deleteReportMutation = useMutation({
    mutationFn: () => reportDetailsApi.deleteReport(reportId as number),
    onSuccess: (res) => {
      if (res?.data?.conflict) return;
      queryClient.removeQueries({ queryKey: ['reportDetails', reportId, lang] });
      queryClient.invalidateQueries({ queryKey: ['reportManagementList'] });
      queryClient.invalidateQueries({ queryKey: ['pscReportsToday'] });
    },
  });

  const completeReportMutation = useMutation({
    mutationFn: () => reportDetailsApi.postReportComplete(reportId as number),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportDetails', reportId, lang] });
      // The operator lands back on the PSC Dashboard right after this -
      // without this, its card list would still be showing the stale
      // "active" snapshot until the next poll interval.
      queryClient.invalidateQueries({ queryKey: ['pscReportsToday'] });
    },
  });

  // A rejected row (whether superseded or never redispatched) doesn't gate
  // completion - only the still-relevant, non-rejected rows must all be
  // AMB_RPT_ST_FIN. Mirrors post_report_complete.py's guard exactly (real
  // enforcement is server-side; this is UX only) - a report where one
  // ambulance was rejected and never redispatched, but the others finished,
  // must still be completable. Already-completed is excluded too, so the
  // button can't be clicked a second time once it's done its job.
  const activeAmbulanceReports = ambulanceReports.filter(
    (ar) => !ar.is_replaced && ar.ambulance_report_status !== 'AMB_RPT_ST_REJ'
  );
  const canCompleteReport =
    report?.report_status !== 'RPT_ST_COMP' &&
    activeAmbulanceReports.length > 0 &&
    activeAmbulanceReports.every((ar) => ar.ambulance_report_status === 'AMB_RPT_ST_FIN');

  return {
    report,
    patients,
    ambulanceReports,
    status,
    images,
    voiceNotes,
    recordings,
    timeline,
    isLoading,
    isDetailError,
    requestLocation: locationRequestMutation.mutate,
    isRequestingLocation: locationRequestMutation.isPending,
    saveReport: saveReportMutation.mutate,
    isSavingReport: saveReportMutation.isPending,
    deleteReport: deleteReportMutation.mutate,
    isDeletingReport: deleteReportMutation.isPending,
    completeReport: completeReportMutation.mutate,
    isCompletingReport: completeReportMutation.isPending,
    canCompleteReport,
  };
};

const photoFileName = (item: AmbulanceReportMediaItem): string =>
  item.media_files_path?.split('/').pop() || 'photo';

export const useAmbulanceReportMedia = (ambulanceReportId?: number) => {
  const enabled = ambulanceReportId != null;
  const queryClient = useQueryClient();

  const mediaQuery = useQuery({
    queryKey: ['ambulanceReportMedia', ambulanceReportId],
    queryFn: async () => {
      const res = await reportDetailsApi.getAmbulanceReportMedia(ambulanceReportId as number);
      return res?.data?.media_list ?? [];
    },
    enabled,
  });

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) =>
      reportDetailsApi.postAmbulanceReportMedia(ambulanceReportId as number, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ambulanceReportMedia', ambulanceReportId] });
    },
  });

  return {
    photos: ((mediaQuery.data as AmbulanceReportMediaItem[]) ?? []).map((item) => ({
      id: item.ambulance_report_media_files_id,
      fileName: photoFileName(item),
      registeredTime: item.registered_time
        ? formatFullDateTimeDDMMYYYY(new Date(item.registered_time))
        : '',
      url: item.link,
    })),
    isLoadingPhotos: mediaQuery.isLoading,
    uploadPhotos: uploadMutation.mutateAsync,
    isUploadingPhotos: uploadMutation.isPending,
  };
};

/**
 * The users a report's ambulance reception officer may be: the PSC operators of
 * the caller's organization. Not report-scoped, so it is one cached list shared
 * by every report the operator opens - staff changes come from User Management,
 * not from anything happening on this page.
 */
export const useReportOfficers = () => {
  const officersQuery = useQuery({
    queryKey: ['reportOfficers'],
    queryFn: async () => {
      const res = await reportDetailsApi.getReportOfficers();
      return res?.data?.officer_list ?? [];
    },
    staleTime: 1000 * 60 * 5,
  });

  return {
    officers: (officersQuery.data as ReportOfficerItem[]) ?? [],
    isLoadingOfficers: officersQuery.isLoading,
  };
};
